import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type ReconciliationRun = {
  id: string;
  trial_id: string;
  trial_site_id: string | null;
  period_start: string;
  period_end: string;
  expected_count: number;
  captured_count: number;
  missed_visit_count: number;
  phantom_count: number;
  duplicate_count: number;
  drift_count: number;
  out_of_window_count: number;
  over_billed_amount: number;
  under_billed_amount: number;
  drift_amount: number;
  accuracy_percent: number | null;
  currency: string;
  status: "running" | "complete" | "failed";
  summary: Record<string, unknown>;
  error_message: string | null;
  generated_by: string | null;
  created_at: string;
  completed_at: string | null;
};

export type ReconciliationFinding = {
  id: string;
  run_id: string;
  patient_id: string | null;
  visit_id: string | null;
  billable_event_id: string | null;
  finding_type:
    | "phantom_billing"
    | "duplicate_billing"
    | "amendment_drift"
    | "out_of_window_visit"
    | "missed_billable"
    | "missed_scheduled_visit";
  severity: "info" | "warning" | "critical";
  expected_amount: number | null;
  actual_amount: number | null;
  variance_amount: number | null;
  currency: string;
  description: string | null;
  details: Record<string, any> | null;
  status: "open" | "resolved" | "waived";
  resolution_notes: string | null;
  created_at: string;
};


export function useReconciliationRuns(trialId?: string, trialSiteId?: string) {
  return useQuery({
    queryKey: ["recon-runs", trialId, trialSiteId],
    enabled: !!trialId,
    queryFn: async () => {
      let q = supabase
        .from("billing_reconciliation_runs")
        .select(
          "id,trial_id,trial_site_id,period_start,period_end,expected_count,captured_count,missed_visit_count,phantom_count,duplicate_count,drift_count,out_of_window_count,over_billed_amount,under_billed_amount,drift_amount,accuracy_percent,currency,status,summary,error_message,generated_by,created_at,completed_at"
        )
        .eq("trial_id", trialId!)
        .order("created_at", { ascending: false })
        .limit(25);
      if (trialSiteId) q = q.eq("trial_site_id", trialSiteId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ReconciliationRun[];
    },
  });
}

export function useReconciliationFindings(runId?: string) {
  return useQuery({
    queryKey: ["recon-findings", runId],
    enabled: !!runId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("billing_reconciliation_findings")
        .select(
          "id,run_id,patient_id,visit_id,billable_event_id,finding_type,severity,expected_amount,actual_amount,variance_amount,currency,description,details,status,resolution_notes,created_at"
        )
        .eq("run_id", runId!)
        .order("severity", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ReconciliationFinding[];
    },
  });
}

export type ReconciliationPreviewFinding = {
  trial_site_id: string | null;
  patient_id: string | null;
  visit_id: string | null;
  billable_event_id: string | null;
  study_visit_id: string | null;
  finding_type: ReconciliationFinding["finding_type"];
  severity: ReconciliationFinding["severity"];
  expected_amount: number | null;
  actual_amount: number | null;
  variance_amount: number | null;
  currency: string;
  description: string | null;
  details: Record<string, any> | null;
};


export type ReconciliationPreview = {
  preview: true;
  currency: string;
  period_start: string;
  period_end: string;
  expected_count: number;
  captured_count: number;
  total_billed: number;
  phantom_count: number;
  duplicate_count: number;
  drift_count: number;
  out_of_window_count: number;
  missed_billable_count: number;
  missed_visit_count: number;
  over_billed_amount: number;
  under_billed_amount: number;
  drift_amount: number;
  accuracy_percent: number | null;
  findings: ReconciliationPreviewFinding[];
};

export function usePreviewReconciliation() {
  return useMutation({
    mutationFn: async (input: {
      trial_id: string;
      trial_site_id: string | null;
      period_start: string;
      period_end: string;
    }) => {
      const { data, error } = await supabase.rpc("preview_billing_reconciliation", {
        p_trial_id: input.trial_id,
        p_trial_site_id: input.trial_site_id,
        p_period_start: input.period_start,
        p_period_end: input.period_end,
      });
      if (error) throw error;
      return data as unknown as ReconciliationPreview;
    },
    onError: (e: Error) => toast.error(e.message || "Preview failed"),
  });
}

export function useRunReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      trial_id: string;
      trial_site_id: string | null;
      period_start: string;
      period_end: string;
    }) => {
      const { data, error } = await supabase.rpc("run_billing_reconciliation", {
        p_trial_id: input.trial_id,
        p_trial_site_id: input.trial_site_id,
        p_period_start: input.period_start,
        p_period_end: input.period_end,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["recon-runs", vars.trial_id] });
      toast.success("Findings saved");
    },
    onError: (e: Error) => toast.error(e.message || "Reconciliation failed"),
  });
}


export function useUpdateFindingStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: "open" | "resolved" | "waived";
      resolution_notes?: string;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("billing_reconciliation_findings")
        .update({
          status: input.status,
          resolution_notes: input.resolution_notes ?? null,
          resolved_by: userData.user?.id ?? null,
          resolved_at: new Date().toISOString(),
        })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recon-findings"] });
      toast.success("Finding updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function findingTypeLabel(t: ReconciliationFinding["finding_type"]) {
  switch (t) {
    case "phantom_billing": return "Phantom billing";
    case "duplicate_billing": return "Duplicate billing";
    case "amendment_drift": return "Amendment drift";
    case "out_of_window_visit": return "Out-of-window visit";
    case "missed_billable": return "Missed billable";
    case "missed_scheduled_visit": return "Missed scheduled visit";
  }
}

export function useRunAuthors(userIds: (string | null)[]) {
  const unique = Array.from(new Set(userIds.filter((u): u is string => !!u)));
  return useQuery({
    queryKey: ["recon-run-authors", unique.sort().join(",")],
    enabled: unique.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name, email")
        .in("user_id", unique);
      if (error) throw error;
      const map: Record<string, { name: string; email: string | null }> = {};
      (data ?? []).forEach((p: any) => {
        const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.email || "Unknown user";
        map[p.user_id] = { name, email: p.email ?? null };
      });
      return map;
    },
  });
}
